import React, { useState, useEffect, useMemo } from 'react';
import { communicationsService, clientsService } from '../../services';
import CommunicationLogger from './CommunicationLogger';

// ─── Config ─────────────────────────────────────────────────────────────────

const COMM_TYPES = {
  email: { label: 'Email', icon: '✉️', color: '#2563EB', bg: 'rgba(37,99,235,0.1)' },
  phone: { label: 'Phone Call', icon: '📞', color: '#059669', bg: 'rgba(5,150,105,0.1)' },
  text: { label: 'Text', icon: '💬', color: '#7C3AED', bg: 'rgba(124,58,237,0.1)' },
  meeting: { label: 'Meeting', icon: '🤝', color: '#C4871F', bg: 'rgba(196,135,31,0.1)' },
  quote_message: { label: 'Quote Message', icon: '📄', color: '#EA580C', bg: 'rgba(234,88,12,0.1)' },
  itinerary_message: { label: 'Itinerary Message', icon: '📄', color: '#EA580C', bg: 'rgba(234,88,12,0.1)' },
  other: { label: 'Note', icon: '📝', color: '#6B7A94', bg: 'rgba(107,122,148,0.1)' },
};

const TYPE_TAB_DEFS = [
  { id: 'all', label: 'All' },
  { id: 'email', label: 'Email' },
  { id: 'phone', label: 'Phone' },
  { id: 'text', label: 'Text' },
  { id: 'meeting', label: 'Meetings' },
  { id: 'quote_message', label: 'Quote Messages' },
  { id: 'inbound', label: 'Inbound' },
  { id: 'outbound', label: 'Outbound' },
  { id: 'follow_up', label: 'Follow-ups' },
];

function parseTags(tags) {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags;
  try {
    const parsed = JSON.parse(tags);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Fall back for any legacy plain comma-separated strings
    return tags.split(',').map(t => t.trim()).filter(Boolean);
  }
}

function isOverdueFollowUp(c) {
  return !!c.follow_up_required && !c.follow_up_completed && c.follow_up_date && new Date(c.follow_up_date) < new Date();
}
function isPendingFollowUp(c) {
  return !!c.follow_up_required && !c.follow_up_completed && !isOverdueFollowUp(c);
}

function formatDateHeader(dateString) {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(date, today)) return 'Today';
  if (sameDay(date, yesterday)) return 'Yesterday';
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' +
    date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// ─── Communication card ─────────────────────────────────────────────────────

function CommCard({ comm, onEdit, onDelete, onMarkFollowUpDone }) {
  const [expanded, setExpanded] = useState(false);
  const ct = COMM_TYPES[comm.communication_type] || COMM_TYPES.other;
  const overdue = isOverdueFollowUp(comm);
  const pending = isPendingFollowUp(comm);
  const tags = parseTags(comm.tags);
  const clientName = [comm.client_first_name, comm.client_last_name].filter(Boolean).join(' ');

  const subject = comm.subject ||
    (comm.communication_type === 'quote_message' && comm.quote_number
      ? `Quote Message - ${comm.quote_number}${comm.quote_title ? ` (${comm.quote_title})` : ''}`
      : `${ct.label} with ${clientName || 'client'}`);

  return (
    <div className="bg-white rounded-2xl border border-[var(--border)] overflow-hidden hover:shadow-md transition-all">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg" style={{ backgroundColor: ct.bg }}>
            {ct.icon}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <div className="flex items-center flex-wrap gap-2">
                <span className="text-sm font-bold text-[var(--foreground)] leading-snug">{subject}</span>
                {comm.quote_number && comm.communication_type !== 'quote_message' && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: 'rgba(234,88,12,0.08)', color: '#EA580C', border: '1px solid rgba(234,88,12,0.2)' }}>
                    Quote #{comm.quote_number}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={onEdit} className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:scale-105"
                  style={{ color: 'var(--foreground)', backgroundColor: 'var(--muted)' }}>
                  Edit
                </button>
                <button onClick={onDelete} className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:scale-105"
                  style={{ color: '#DC2626', backgroundColor: 'rgba(220,38,38,0.06)' }}>
                  Delete
                </button>
              </div>
            </div>

            <div className="flex items-center flex-wrap gap-2 mb-2">
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                style={comm.direction === 'outbound'
                  ? { backgroundColor: 'rgba(196,135,31,0.1)', color: '#C4871F' }
                  : { backgroundColor: 'rgba(37,99,235,0.1)', color: '#2563EB' }}>
                {comm.direction === 'outbound' ? <><span>↑</span> Outbound</> : <><span>↓</span> Inbound</>}
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: ct.bg, color: ct.color }}>
                {ct.label}
              </span>
              <span className="text-[11px] text-[var(--muted-foreground)]">{formatDateTime(comm.communication_date)}</span>
              {!!comm.duration_minutes && <span className="text-[11px] text-[var(--muted-foreground)]">· {comm.duration_minutes}min</span>}
              {comm.workflow_stage_name && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                  {comm.workflow_stage_name}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 text-xs text-[var(--muted-foreground)] mb-2">
              <span className="flex items-center gap-1 font-semibold text-[var(--foreground)]">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                {clientName || 'Unknown client'}
              </span>
              {comm.related_task_title && (
                <span className="flex items-center gap-1">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
                  {comm.related_task_title}
                </span>
              )}
            </div>

            {comm.content && (
              <div className="mb-2">
                <p className={`text-xs text-[var(--muted-foreground)] leading-relaxed ${!expanded ? 'line-clamp-2' : ''}`}>{comm.content}</p>
                {comm.content.length > 120 && (
                  <button onClick={() => setExpanded(p => !p)} className="text-[11px] font-semibold mt-0.5 transition-colors" style={{ color: 'var(--accent)' }}>
                    {expanded ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
            )}

            <div className="flex items-center flex-wrap gap-2">
              {tags.map(tag => (
                <span key={tag} className="px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>#{tag}</span>
              ))}
              {(overdue || pending) && (
                <div className="flex items-center gap-1.5 ml-auto">
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                    style={overdue ? { backgroundColor: 'rgba(220,38,38,0.1)', color: '#DC2626' } : { backgroundColor: 'rgba(196,135,31,0.1)', color: '#C4871F' }}>
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                    {overdue ? 'Follow-up overdue' : 'Follow-up pending'}
                  </span>
                  <button onClick={onMarkFollowUpDone} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold transition-all hover:brightness-95"
                    style={{ backgroundColor: 'rgba(5,150,105,0.1)', color: '#059669' }}>
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                    Done
                  </button>
                </div>
              )}
              {!!comm.follow_up_completed && (
                <span className="ml-auto flex items-center gap-1 text-[10px] font-semibold" style={{ color: '#059669' }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                  Follow-up complete
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

const CommunicationsHistory = () => {
  const [communications, setCommunications] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedClient, setSelectedClient] = useState('');
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCommunication, setEditingCommunication] = useState(null);

  useEffect(() => {
    fetchCommunications();
    fetchStats();
    fetchClients();
  }, [typeFilter, selectedClient, search]);

  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); };

  const fetchCommunications = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (selectedClient) filters.client_id = selectedClient;
      if (typeFilter === 'email') filters.communication_type = 'email';
      if (typeFilter === 'phone') filters.communication_type = 'phone';
      if (typeFilter === 'text') filters.communication_type = 'text';
      if (typeFilter === 'meeting') filters.communication_type = 'meeting';
      if (typeFilter === 'quote_message') filters.communication_type = 'quote_message';
      if (typeFilter === 'inbound') filters.direction = 'inbound';
      if (typeFilter === 'outbound') filters.direction = 'outbound';
      if (typeFilter === 'follow_up') filters.follow_up_required = 'true';
      if (search) filters.search = search;

      setCommunications(await communicationsService.getCommunications(filters));
    } catch (err) {
      console.error('Failed to fetch communications:', err);
      setError('Failed to load communications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try { setStats(await communicationsService.getCommunicationStats()); }
    catch (err) { console.error('Failed to fetch communication statistics:', err); }
  };

  const fetchClients = async () => {
    try { setClients(await clientsService.getAll()); }
    catch (err) { console.error('Failed to fetch clients:', err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this communication record?')) return;
    try {
      await communicationsService.deleteCommunication(id);
      showSuccess('Communication deleted.');
      await fetchCommunications();
      await fetchStats();
    } catch (err) {
      console.error('Failed to delete communication:', err);
      setError('Failed to delete communication. Please try again.');
    }
  };

  const handleMarkFollowUpDone = async (id) => {
    try {
      await communicationsService.markFollowUpDone(id);
      showSuccess('Follow-up marked as complete.');
      await fetchCommunications();
      await fetchStats();
    } catch (err) {
      console.error('Failed to mark follow-up done:', err);
      setError('Failed to update follow-up. Please try again.');
    }
  };

  const TYPE_TABS = TYPE_TAB_DEFS.map(t => ({
    ...t,
    count: {
      all: stats.total_communications, email: stats.email_count, phone: stats.phone_count,
      text: stats.text_count, meeting: stats.meeting_count, quote_message: stats.quote_message_count,
      inbound: stats.inbound_count, outbound: stats.outbound_count, follow_up: stats.pending_followups,
    }[t.id] || 0,
  }));

  // Group by date (backend already sorts by communication_date DESC)
  const grouped = useMemo(() => {
    const groups = [];
    for (const comm of communications) {
      const label = formatDateHeader(comm.communication_date);
      const last = groups[groups.length - 1];
      if (last && last.label === label) last.items.push(comm);
      else groups.push({ label, items: [comm] });
    }
    return groups;
  }, [communications]);

  if (loading && communications.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto" style={{ borderColor: 'var(--accent)' }}></div>
          <p className="mt-4 text-sm text-[var(--muted-foreground)]">Loading communications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {success && (
        <div className="fixed top-5 right-5 z-50 flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white" style={{ backgroundColor: 'var(--primary)' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
          {success}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]" style={{ fontFamily: 'var(--font-display)' }}>Communication History</h1>
          <p className="text-sm text-[var(--muted-foreground)] mt-1">Track all client communications and follow-ups.</p>
        </div>
        <button onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:brightness-105" style={{ backgroundColor: 'var(--primary)' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          Record Communication
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm text-red-700 bg-red-50 border border-red-200">{error}</div>
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: stats.total_communications || 0, color: 'var(--primary)', bg: 'rgba(26,43,74,0.08)' },
          { label: 'Clients Contacted', value: stats.clients_communicated_with || 0, color: '#2563EB', bg: 'rgba(37,99,235,0.1)' },
          { label: 'Pending Follow-ups', value: stats.pending_followups || 0, color: '#C4871F', bg: 'rgba(196,135,31,0.1)' },
          { label: 'Overdue Follow-ups', value: stats.overdue_followups || 0, color: '#DC2626', bg: 'rgba(220,38,38,0.1)' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-[var(--border)] p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-xl flex-shrink-0"
              style={{ fontFamily: 'var(--font-display)', backgroundColor: s.bg, color: s.color }}>
              {s.value}
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]">{s.label}</p>
              {s.label === 'Overdue Follow-ups' && s.value > 0 && <p className="text-[10px] text-red-500 font-semibold">Needs attention</p>}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <select value={selectedClient} onChange={e => setSelectedClient(e.target.value)}
            className="px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[rgba(196,135,31,0.25)] cursor-pointer flex-shrink-0"
            style={{ minWidth: '180px' }}>
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
          </select>
          <div className="relative flex-1" style={{ minWidth: '220px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--muted-foreground)" strokeWidth="2" className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by subject, client, notes, or tag…"
              className="w-full pl-10 pr-4 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[rgba(196,135,31,0.25)] focus:border-[var(--accent)] transition-all" />
          </div>
        </div>

        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {TYPE_TABS.map(tab => (
            <button key={tab.id} onClick={() => setTypeFilter(tab.id)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap flex-shrink-0 transition-all"
              style={typeFilter === tab.id ? { backgroundColor: 'var(--primary)', color: 'white' } : { color: 'var(--muted-foreground)', backgroundColor: 'white', border: '1px solid var(--border)' }}>
              {tab.label}
              {tab.count > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black"
                  style={typeFilter === tab.id
                    ? { backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }
                    : { backgroundColor: tab.id === 'follow_up' && stats.overdue_followups > 0 ? 'rgba(220,38,38,0.1)' : 'var(--muted)', color: tab.id === 'follow_up' && stats.overdue_followups > 0 ? '#DC2626' : 'var(--muted-foreground)' }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Timeline */}
      {grouped.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[var(--border)] px-6 py-12 text-center">
          <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: 'var(--muted)' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--muted-foreground)" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
          </div>
          <p className="text-sm font-semibold text-[var(--muted-foreground)]">No communications found</p>
          <p className="text-xs text-[var(--muted-foreground)] mt-1">
            {search || selectedClient || typeFilter !== 'all' ? 'Try adjusting your filters' : 'Record your first communication to get started'}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map(group => (
            <div key={group.label}>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px flex-1" style={{ backgroundColor: 'var(--border)' }} />
                <span className="text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--primary)', color: 'white' }}>
                  {group.label}
                </span>
                <div className="h-px flex-1" style={{ backgroundColor: 'var(--border)' }} />
              </div>
              <div className="space-y-3">
                {group.items.map(comm => (
                  <CommCard key={comm.id} comm={comm}
                    onEdit={() => setEditingCommunication(comm)}
                    onDelete={() => handleDelete(comm.id)}
                    onMarkFollowUpDone={() => handleMarkFollowUpDone(comm.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {communications.length > 0 && (
        <p className="text-center text-xs text-[var(--muted-foreground)] py-4">
          Showing {communications.length} communication{communications.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Modals */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15,28,46,0.6)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CommunicationLogger
              onClose={() => setShowCreateModal(false)}
              onSaved={async () => {
                setShowCreateModal(false);
                showSuccess('Communication recorded.');
                await fetchCommunications();
                await fetchStats();
              }}
            />
          </div>
        </div>
      )}

      {editingCommunication && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15,28,46,0.6)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CommunicationLogger
              communication={editingCommunication}
              onClose={() => setEditingCommunication(null)}
              onSaved={async () => {
                setEditingCommunication(null);
                showSuccess('Communication updated.');
                await fetchCommunications();
                await fetchStats();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationsHistory;
